import React, { useCallback, useState } from "react";

import { RowAction } from "@tiny/components/src/ListItemRow/ListItemRow.types";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { useSuppressionListView } from "../SuppressionList.context";
import {
  getTimeActionsInfo,
  toChipColor,
  toChipText,
  downloadFileFromUrl,
} from "./SuppressionListRow.helper";
import { SuppressionListRowProps } from "./SuppressionListRow.types";
import useTinyImporter from "../../../hooks/useTinyImporter";
import { useTinyHost } from "@tiny/host-contract";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { useApi, SuppressionFileView } from "@tiny/api";
import { closeSnackbar } from "notistack";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { useConfirm, useMessages } from "@tiny/components";

export function useSuppressionListRow(props: SuppressionListRowProps) {
  const api = useApi();
  const { tinyImporterOpen, closeTinyImporter, openTinyImporter } =
    useTinyImporter();

  const { showErrorMessage } = useMessages();
  const showConfirm = useConfirm();

  const {
    suppression,
    seeAllSubcribersOnUpload = false,
    canToggleScope = true,
    isCampaignMode = false,
  } = props;

  const [toggleSuppressionDialogOpen, setToggleSuppressionDialogOpen] =
    useState(false);

  const { mdUp } = useBreakpoints();

  const { navigate } = useTinyHost();

  const { isSuppressionDeletable, selectionDisabled } =
    useSuppressionListView();

  const {
    enableBulkDelete,
    toggleSelectItem,
    isItemSelected,
    sortOption,
    onDeleteSuppression,
    reloadSuppressionList,
  } = useSuppressionListView();

  const [detailsOpen, setDetailsOpen] = React.useState(false);

  const [filesDialogOpen, setFilesDialogOpen] = React.useState(false);

  const [suppressionFiles, setSuppressionFiles] = React.useState<
    SuppressionFileView[]
  >([]);

  const closeFilesDialog = React.useCallback(() => {
    setFilesDialogOpen(false);
  }, []);

  const [appendImporterOpen, setAppendImporterOpen] = React.useState(false);

  const openAppendImporter = React.useCallback(() => {
    setAppendImporterOpen(true);
  }, []);

  const closeAppendImporter = React.useCallback(() => {
    setAppendImporterOpen(false);
  }, []);

  const [moreOpen, setMoreOpen] = React.useState(false);

  const [mutationLoading, setMutationLoading] = React.useState(false);

  const moreButtonRef = React.useRef<HTMLButtonElement | null>(null);

  const showMore = React.useCallback(() => {
    setMoreOpen(true);
  }, []);

  const hideMore = React.useCallback(() => {
    setMoreOpen(false);
  }, []);

  const toggleDetails = React.useCallback(() => {
    setDetailsOpen((prev) => !prev);
  }, []);

  const deletable = React.useMemo(
    () => isSuppressionDeletable(suppression),
    [suppression, isSuppressionDeletable]
  );

  const onSelect = React.useCallback(
    () => toggleSelectItem(suppression),
    [suppression, toggleSelectItem]
  );

  const isSelected = React.useMemo(
    () =>
      enableBulkDelete && !deletable
        ? undefined
        : () => isItemSelected(suppression),
    [suppression, deletable, enableBulkDelete, isItemSelected]
  );

  const disabledCheckbox = React.useMemo(
    () => (enableBulkDelete && !deletable) || selectionDisabled,
    [deletable, enableBulkDelete, selectionDisabled]
  );

  const timeActions = React.useMemo(
    () => getTimeActionsInfo(mdUp, suppression, sortOption.action),
    [suppression, mdUp, sortOption.action]
  );

  const chipColor = React.useMemo(
    () => toChipColor(suppression.scope),
    [suppression.scope]
  );

  const chipText = React.useMemo(
    () => toChipText(suppression.scope),
    [suppression.scope]
  );

  const subscribersText = React.useMemo(
    () => `Customer${suppression.suppressedCount === 1 ? "" : "s"}`,
    [suppression.suppressedCount]
  );

  const confirmAndDownloadSingle = React.useCallback(
    (url: string, fileName: string) => {
      showConfirm({
        title: `Do you want to download ${suppression.name} suppression list ?`,
        message: "The suppression list will be downloaded to your device.",
        actions: [
          {
            label: "No",
            onClick: () => {
              return;
            },
          },
          {
            label: "Yes",
            primary: true,
            onClick: async () => {
              try {
                showUploadMessage({
                  variant: "uploading",
                  message: "Downloading",
                  description: "Your suppression list is downloading",
                  persist: true,
                  showClose: true,
                });

                await downloadFileFromUrl(url, fileName);

                closeSnackbar();
                showUploadMessage({
                  variant: "success",
                  message: "Downloaded successfully!",
                  description:
                    "Your suppression list has been downloaded successfully!",
                  primaryCtaActionText: "Close",
                  primaryCtaActionFunction: () => {
                    closeSnackbar();
                  },
                  persist: false,
                });
              } catch (e: any) {
                closeSnackbar();
                extractBackendError(e.response).then((be) => {
                  showErrorMessage(getErrorMessage(be));
                });
              }
            },
          },
        ],
      });
    },
    [showConfirm, showErrorMessage, suppression.name]
  );

  const downloadSuppressionDetails = React.useCallback(
    async (suppressionId: string) => {
      try {
        showUploadMessage({
          variant: "uploading",
          message: "Loading",
          description: "Fetching your suppression files",
          persist: true,
          showClose: true,
        });

        const { data } = await api.getSuppressionFiles(suppressionId);
        const files = data?.files || [];
        closeSnackbar();

        if (files.length === 0) {
          showErrorMessage("No files found for this suppression list.");
          return;
        }

        // Multiple files: show the picker list directly (no confirm).
        if (files.length > 1) {
          setSuppressionFiles(files);
          setFilesDialogOpen(true);
          return;
        }

        // Exactly one file: confirm, then download.
        confirmAndDownloadSingle(files[0].url || "", files[0].fileName || "");
      } catch (e: any) {
        closeSnackbar();
        extractBackendError(e.response).then((be) => {
          showErrorMessage(getErrorMessage(be));
        });
      }
    },
    [confirmAndDownloadSingle, showErrorMessage]
  );

  const moreActions = React.useMemo(() => {
    const deleteAction = {
      name: "Delete",
      onClickAction: async () => {
        setMutationLoading(true);
        try {
          await onDeleteSuppression(suppression);
        } finally {
          setMutationLoading(false);
        }
      },
    };

    const markAsGlobal = {
      name: suppression.scope === "LOCAL" ? "Mark as global" : "Mark as local",
      onClickAction: () => {
        openToggleSuppressionDialog();
      },
    };

    const download = {
      name: "Download",
      onClickAction: () => downloadSuppressionDetails(suppression.id || ""),
    };

    const addToList = {
      name: "Add to list",
      onClickAction: () => openAppendImporter(),
    };

    const rowActions: RowAction[] = [addToList, download];
    if (deletable) {
      rowActions.push(deleteAction);
    }

    if (canToggleScope) {
      rowActions.push(markAsGlobal);
    }
    return rowActions;
  }, [
    suppression,
    deletable,
    canToggleScope,
    onDeleteSuppression,
    downloadSuppressionDetails,
    openAppendImporter,
  ]);

  const checked = isSelected ? isSelected() : false;

  const audienceLocation = {
    pathname: `/main/audience/suppression/${suppression.id}`,
    state: {
      backUrl: { pathname: location.pathname, search: location.search },
    },
  };

  const openToggleSuppressionDialog = () => {
    setToggleSuppressionDialogOpen(true);
  };

  const closeToggleSuppressionDialog = () => {
    setToggleSuppressionDialogOpen(false);
  };

  const isCheckboxDisabled = React.useMemo(() => {
    if (isCampaignMode) {
      return (
        suppression.scope === "GLOBAL" ||
        suppression.fileProcessStatus !== "COMPLETED" ||
        disabledCheckbox
      );
    }
    return disabledCheckbox;
  }, [
    isCampaignMode,
    disabledCheckbox,
    suppression.scope,
    suppression.fileProcessStatus,
  ]);

  const isCheckboxChecked = React.useMemo(() => {
    if (isCampaignMode && suppression.scope === "GLOBAL") {
      return true;
    }
    return checked;
  }, [isCampaignMode, suppression.scope, checked]);

  const handleSelect = React.useCallback(() => {
    if (isCampaignMode) {
      if (
        suppression.scope !== "GLOBAL" &&
        suppression.fileProcessStatus === "COMPLETED"
      ) {
        onSelect();
      }
    } else {
      onSelect();
    }
  }, [
    isCampaignMode,
    suppression.scope,
    suppression.fileProcessStatus,
    onSelect,
  ]);

  return {
    onSelect,
    subscribersText,
    disabledCheckbox,
    sortAction: sortOption.action,
    timeActions,
    chipColor,
    chipText,
    moreActions,
    detailsOpen,
    moreButtonRef,
    checked,
    moreOpen,
    mutationLoading,
    toggleDetails,
    showMore,
    hideMore,
    selectionDisabled,
    audienceLocation,
    tinyImporterOpen,
    openTinyImporter,
    closeTinyImporter,
    openToggleSuppressionDialog,
    closeToggleSuppressionDialog,
    toggleSuppressionDialogOpen,
    downloadSuppressionDetails,
    isCheckboxChecked,
    isCheckboxDisabled,
    handleSelect,
    appendImporterOpen,
    openAppendImporter,
    closeAppendImporter,
    reloadSuppressionList,
    filesDialogOpen,
    closeFilesDialog,
    suppressionFiles,
  };
}
